import { Mail, ExternalLink } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const DISCORD_URL = process.env.REACT_APP_DISCORD_URL || 'https://discord.gg/hosting-community';

function DiscordIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
    </svg>
  );
}

export function ListingCTA() {
  const { t } = useLanguage();
  const l = t.listing;

  return (
    <section
      id="anbieter-werden"
      className="py-20 md:py-28 bg-[#020817] relative overflow-hidden"
      data-testid="listing-cta-section"
    >
      {/* Background accent */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-cyan-500/5 blur-[100px] rounded-full" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <span
            data-testid="listing-badge"
            className="inline-flex items-center gap-2 px-3 py-1 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-xs font-semibold text-cyan-400 uppercase tracking-wider mb-4"
          >
            {l.badge}
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
            {l.title}
          </h2>
          <p className="text-base text-slate-400 max-w-xl mx-auto leading-relaxed">
            {l.subtitle}
          </p>
        </div>

        {/* Two option cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">

          {/* Email card */}
          <div
            data-testid="listing-email-card"
            className="group relative p-7 rounded-2xl bg-[#0f172a] border border-slate-800 hover:border-cyan-500/40 hover:shadow-[0_0_30px_rgba(0,188,212,0.08)] transition-all duration-300"
          >
            <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-5 group-hover:bg-cyan-500/20 transition-colors">
              <Mail className="w-6 h-6 text-cyan-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">{l.option1_title}</h3>
            <p className="text-sm text-slate-400 leading-relaxed mb-6">{l.option1_desc}</p>
            <a
              href={`mailto:${l.option1_address}?subject=Anbieter-Anfrage – Hosting-Community`}
              data-testid="listing-email-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-cyan-500 text-slate-950 font-semibold rounded-xl text-sm hover:bg-cyan-400 transition-colors shadow-[0_0_20px_rgba(0,188,212,0.25)]"
            >
              {l.option1_cta}
              <Mail className="w-4 h-4" />
            </a>
            <p className="mt-3 text-xs text-slate-500 font-mono">{l.option1_address}</p>
          </div>

          {/* Discord card */}
          <div
            data-testid="listing-discord-card"
            className="group relative p-7 rounded-2xl bg-[#0f172a] border border-slate-800 hover:border-[#5865F2]/40 hover:shadow-[0_0_30px_rgba(88,101,242,0.08)] transition-all duration-300"
          >
            <div className="w-12 h-12 rounded-xl bg-[#5865F2]/10 flex items-center justify-center mb-5 group-hover:bg-[#5865F2]/20 transition-colors">
              <DiscordIcon className="w-6 h-6 text-[#7289da]" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">{l.option2_title}</h3>
            <p className="text-sm text-slate-400 leading-relaxed mb-6">{l.option2_desc}</p>
            <a
              href={DISCORD_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="listing-discord-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#5865F2] text-white font-semibold rounded-xl text-sm hover:bg-[#4752c4] transition-colors"
            >
              {l.option2_cta}
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>

        {/* Note */}
        <p
          data-testid="listing-note"
          className="text-center text-xs text-slate-600 mt-8 max-w-lg mx-auto"
        >
          {l.note}
        </p>
      </div>
    </section>
  );
}
